import styled from "styled-components"

import {
  Checkbox,
  colors,
  Select,
  spacing,
  Text,
  TextArea,
  TextField,
  TSelectOptions,
} from "@minuthq/meatball-ui-react"
import { FormikProps } from "formik"

import { HomesDropdown } from "src/components/Dropdown/HomesDropdown"
import { ParadiseNativeDateField } from "src/components/Paradise/NativeDateTimeField"
import { TMaybeHome } from "src/data/homes/types/homeTypes"
import { useTaskTypeLabels } from "src/data/taskManagement/hooks/useFetchTaskTableData"
import { isUnassignedOptionId } from "src/data/taskManagement/taskManagementFilters"
import {
  TTaskPostBody,
  TTaskType,
} from "src/data/taskManagement/taskManagementTypes"
import { useTranslate } from "src/i18n/useTranslate"

import { AssigneeCombobox } from "./AssigneeCombobox"

export function TaskForm({
  form,
  homes,
  mode,
}: {
  form: FormikProps<TTaskPostBody>
  homes?: TMaybeHome[]
  mode: "create" | "edit"
}) {
  const { tPlaceholder } = useTranslate()

  const taskTypes = useTaskTypeLabels()

  const homeOption = homes?.find((h) => h?.home_id === form.values.home_id)

  const taskCategory: TSelectOptions<TTaskType> = Object.entries(taskTypes).map(
    ([type, label]) => ({
      label,
      value: type as TTaskType,
    })
  )

  return (
    <FormBox>
      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Title")}
        </Text>
        <TextField
          value={form.values.title}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              title: value,
            }))
          }}
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Unit")}
        </Text>

        <HomesDropdown
          value={homeOption ?? null}
          onSelect={(home) => {
            form.setValues((prev) => ({
              ...prev,
              home_id: home?.home_id,
            }))
          }}
          required={true}
          hideLabel={true}
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Task type")}
        </Text>
        <Select
          value={form.values.category}
          options={taskCategory}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              category: value as TTaskPostBody["category"],
            }))
          }}
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Description")}
        </Text>
        <TextArea
          value={form.values.description}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              description: value,
            }))
          }}
          minRows={3}
          maxRows={5}
        />
      </div>

      <FormRow>
        <div>
          <Text variant="subtitle" marginBottom={spacing.XS2}>
            {tPlaceholder("Due date")}
          </Text>

          <ParadiseNativeDateField
            value={
              form.values.time_window.due_date
                ? new Date(form.values.time_window.due_date)
                : null
            }
            onChange={(value) => {
              form.setValues((prev) => ({
                ...prev,
                time_window: {
                  ...prev.time_window,
                  due_date: value.toISOString(),
                },
              }))
            }}
            required
          />
        </div>

        <div>
          <Text variant="subtitle" marginBottom={spacing.XS2}>
            {tPlaceholder("Assign to")}
          </Text>

          <AssigneeCombobox
            initialValue={form.values.assigned_to?.assignee_id ?? ""}
            onChange={(value) => {
              if (isUnassignedOptionId(value)) {
                form.setValues((prev) => ({
                  ...prev,
                  assigned_to: undefined,
                }))
              } else {
                form.setValues((prev) => ({
                  ...prev,
                  assigned_to: {
                    type: "member",
                    assignee_id: value,
                  },
                }))
              }
            }}
          />
        </div>
      </FormRow>

      {mode === "edit" && (
        <CompleteBox>
          <Checkbox
            checked={form.values.status === "done"}
            onCheck={(checked) =>
              form.setValues((prev) => ({
                ...prev,
                status: checked ? "done" : "todo",
              }))
            }
            label={tPlaceholder("Mark task as complete")}
          />
        </CompleteBox>
      )}
    </FormBox>
  )
}

const FormBox = styled.div`
  display: grid;
  gap: ${spacing.L};
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.M};
`

const CompleteBox = styled.div`
  display: flex;
  border: 1px solid ${colors.divider};
  border-radius: 8px;
  padding: ${spacing.M};
`
